import { useEffect } from 'react';
import type { CountryCode, Movie } from '@filmsloth/shared';
import { useMovieTitle, useT } from '../i18n/index.js';
import { haptic } from '../lib/haptics.js';
import { movieMeta } from '../lib/movieMeta.js';
import { Poster } from './Poster.js';
import { ShareButton } from './ShareButton.js';
import { WatchOnLinks } from './WatchOnLinks.js';

export function MatchOverlay({
  movie,
  country,
  isHost,
  hostName,
  onWatch,
  onContinue,
}: {
  movie: Movie;
  country: CountryCode;
  isHost: boolean;
  hostName: string;
  onWatch: () => void;
  onContinue: () => void;
}) {
  const t = useT();
  const movieTitle = useMovieTitle();

  // The one moment everybody in the room reacts to, and the one the phone is
  // most likely to be face down for. Keyed on the movie, so a second match
  // found while this one is still open buzzes again rather than passing
  // silently.
  useEffect(() => {
    haptic('match');
  }, [movie.id]);

  return (
    <div className="overlay">
      <div className="modal" role="dialog" aria-modal="true" aria-label={t('match.title')}>
        <h2>{t('match.title')}</h2>
        <p className="muted">{t('match.body')}</p>
        <div className="row">
          <Poster movie={movie} size="sm" />
          <div>
            <div>{movieTitle(movie)}</div>
            <div className="small muted">{movieMeta(movie, t)}</div>
          </div>
        </div>
        <WatchOnLinks movie={movie} country={country} />
        {isHost ? (
          <div className="stack">
            <button type="button" className="btn btn--primary" onClick={onWatch}>
              {t('match.hostWatch')}
            </button>
            <button type="button" className="btn" onClick={onContinue}>
              {t('match.hostContinue')}
            </button>
            <ShareButton movie={movie} className="btn btn--ghost" />
          </div>
        ) : (
          <div className="stack">
            <p className="muted center">{t('match.waitingForHost', { name: hostName })}</p>
            <ShareButton movie={movie} className="btn btn--ghost" />
          </div>
        )}
      </div>
    </div>
  );
}
